import { FormsModule } from '@angular/forms';
import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MailFolderContract } from '@app-types/api/mail-folder';
import { MailFolderService } from '@app-services/mail-folder/mail.folder.service';
import { EmailClient } from '@app-services/api/clients/email-client';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SomethingWentWrongComponent } from '../../common/error/something-went-wrong/something-went-wrong.component';
import { ErrorHintComponent } from '../../common/error-hint/error-hint.component';
import { LoadingButtonComponent } from '../../common/loading-button/loading-button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ErrorsPipe } from '@app-pipes/error-code.pipe';
import {
  SearchableSelectComponent,
  SearchableSelectOption,
} from '../../common/searchable-select/searchable-select.component';

@Component({
  selector: 'app-mail-folder-select-dialog',
  templateUrl: './mail-folder-select-dialog.component.html',
  styleUrls: ['./mail-folder-select-dialog.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    SomethingWentWrongComponent,
    ErrorHintComponent,
    LoadingButtonComponent,
    TranslateModule,
    NgxSkeletonLoaderModule,
    SearchableSelectComponent,
    FormsModule,
  ],
})
export class MailFolderSelectDialogComponent implements OnInit {
  public isLoading = true;
  public cannotLoad = false;
  public moveInProgress = false;
  public cannotSave = false;
  public errorText = '';
  public folders: MailFolderContract[] = [];
  public folderOptions: SearchableSelectOption<number>[] = [];
  public selectedFolderId: number | null = null;
  pipeErrors = new ErrorsPipe();

  constructor(
    public matchError: MatchError,
    private mailFolderService: MailFolderService,
    private emailClient: EmailClient,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<MailFolderSelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      mailAccountId: number;
      ids: number[];
      isRestore: boolean;
      title: string;
      folderType: number;
      isArchive: boolean;
      sourceFoldersType: number;
      isTrash: boolean;
    }
  ) {}

  async ngOnInit(): Promise<any> {
    await this.loadFolders();
  }

  async loadFolders(): Promise<void> {
    this.isLoading = true;
    this.cannotLoad = false;
    try {
      const response = await this.mailFolderService.getFoldersForAccountFlat(
        this.data.mailAccountId
      );

      if (this.data.isArchive === true) {
        this.folders = response.filter(
          e => e.folderType === this.data.sourceFoldersType
        );
      } else if (this.data.isTrash === true) {
        this.folders = response.filter(
          e => e.folderType === this.data.sourceFoldersType
        );
      } else if (this.data.folderType === MailFolderType.Spam) {
        this.folders = response.filter(
          e => e.folderType === MailFolderType.Inbox
        );
      } else {
        this.folders = response.filter(
          e => e.folderType === this.data.folderType
        );
      }

      this.folderOptions = this.folders.map(folder => ({
        value: folder.mailFolderId,
        label: this.getFolderLabel(folder),
      }));

      if (this.folders.length > 0) {
        this.selectedFolderId = this.folders[0].mailFolderId;
      }
    } catch (e) {
      this.cannotLoad = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }

  async confirm(): Promise<void> {
    if (!this.selectedFolderId) {
      return;
    }

    if (!this.data.isRestore) {
      this.moveInProgress = true;
      this.cannotSave = false;
      this.errorText = '';

      try {
        await this.emailClient.move({
          emailIds: this.data.ids,
          mailFolderId: this.selectedFolderId,
        });
        this.dialogRef.close({
          confirmed: true,
          folderId: this.selectedFolderId,
        });
      } catch (e: any) {
        this.cannotSave = true;
        this.errorText =
          this.pipeErrors.transform(e?.Code ?? e?.code) ||
          'cannotMoveErrorPopupSubtitle';

        this.matchError.logError(e);
      } finally {
        this.moveInProgress = false;
      }
    }
    if (this.data.isRestore) {
      this.dialogRef.close({
        confirmed: true,
        folderId: this.selectedFolderId,
      });
    }
  }

  cancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  private getFolderLabel(folder: MailFolderContract): string {
    if (folder.creationType === 1) {
      return this.translate.instant('folderType' + folder.folderType);
    }

    return folder.name;
  }
}
